import { observer } from 'mobx-react';
import React, { type FC, type ReactElement, useState } from 'react';
import { useStores } from '../../utils/hooks/useStores';
import {
  Alert,
  Box,
  ContentBlock,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';

import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import { isANZSite, isJobStreetSite } from 'src/utils/site';
import { format } from 'date-fns/format';
import { languageToLocaleMap } from '../common/TimeAgo/timeUtils';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { FilterFns } from '../app/FilterSideBar/useFilters';
import type { FiltersEventCaptureData } from 'src/services/jobApplicationService/buildCaptureEventData';
import { useDeepLinkedDetailView } from 'src/pages/CandidateManagementPage/utils/useDeepLinkedDetailView';
import JobApplicationListSection from 'src/App/JobApplicationListSection';
import DetailsView from '../details/DetailsView';
import type { UseHandleStatusChangeResponse } from 'src/App/hooks/useHandleStatusChange';
import type { EmployerLocale } from 'src/siteConfig';

const JobsDBJobsWithoutMigrationDateObject = new Date('2021-12-31');
const JobStreetJobsWithoutMigrationDateObject = new Date('2022-10-01');

const formatDateInLocale = ({
  date,
  locale,
}: {
  date: Date;
  locale: EmployerLocale;
}) =>
  format(date, 'dd MMM yyyy', {
    locale: languageToLocaleMap[locale],
  });

const BannerComponent = ({
  site,
  locale,
}: {
  site: string;
  locale: EmployerLocale;
}) => {
  const [shouldShowAlert, setShouldShowAlert] = useState(true);
  const { t } = useTranslations(translations);

  if (!shouldShowAlert) {
    return null;
  }

  return (
    <Box paddingY="medium">
      <Alert
        tone="info"
        onClose={() => setShouldShowAlert(false)}
        closeLabel="close-missing-application-alert"
      >
        <Text>
          {t('Applications received before message', {
            formattedDate: isJobStreetSite(site)
              ? formatDateInLocale({
                  date: JobStreetJobsWithoutMigrationDateObject,
                  locale,
                })
              : formatDateInLocale({
                  date: JobsDBJobsWithoutMigrationDateObject,
                  locale,
                }),
          })}
        </Text>
      </Alert>
    </Box>
  );
};

interface CandidateListProps {
  actionToolbar: ReactElement;
  reloadJobApplicationsWithLastResultSearchText: () => void;
  clearTextAndReloadJobApplications: () => void;
  openBillableDialog: () => void;
  useFiltersFns: FilterFns;
  isMustHaveToggleSelected: boolean;
  filtersEventCaptureData: FiltersEventCaptureData;
  handleStatusChange: UseHandleStatusChangeResponse['handleStatusChange'];
  refetchFacets: () => Promise<void>;
  homeLocationIdsForTracking: string[];
}

export const CandidateList: FC<CandidateListProps> = ({
  actionToolbar,
  reloadJobApplicationsWithLastResultSearchText,
  clearTextAndReloadJobApplications,
  openBillableDialog,
  useFiltersFns,
  isMustHaveToggleSelected,
  filtersEventCaptureData,
  handleStatusChange,
  refetchFacets,
  homeLocationIdsForTracking,
}) => {
  const store = useStores();
  const { t } = useTranslations(translations);
  const [
    lastOpenedApplicationCardStateForTracking,
    setLastOpenedApplicationCardStateForTracking,
  ] = useState<boolean | undefined>(undefined);

  const { closeDeepLinkedDetailView } = useDeepLinkedDetailView();

  const { jobApplicationStore, uiStore, isMobile, jobStore } = store;
  const {
    job: {
      jobInformation: { questionnaireId },
    },
  } = jobStore;
  const { site, locale } = useAppConfig();

  const getMigrationCutOffDate = () => {
    if (isJobStreetSite(site)) {
      return JobStreetJobsWithoutMigrationDateObject;
    }
    return JobsDBJobsWithoutMigrationDateObject;
  };

  const migrationCutOffDate = getMigrationCutOffDate();

  const isMigrationAffected =
    !isANZSite(site) &&
    new Date(jobStore.job.jobInformation.positionCreatedDate) <
      migrationCutOffDate;

  const bucket = jobApplicationStore.bucket;

  return (
    <ContentBlock width="large">
      {bucket === BUCKETS.INBOX && isMigrationAffected && (
        <BannerComponent site={site} locale={locale} />
      )}
      {jobApplicationStore.isError ? (
        <Box width="full" data-cy="global-error" margin="xsmall">
          <Alert tone="critical">
            <Box
              display="flex"
              alignItems="flexStart"
              justifyContent="flexStart"
            >
              {jobApplicationStore.lastResultSearchText === '' ? (
                <Text>
                  {t('Error loading the list...', {
                    TextLinkButton: (children) => (
                      <TextLinkButton
                        key={`error-loading-${children}`}
                        onClick={() =>
                          reloadJobApplicationsWithLastResultSearchText()
                        }
                      >
                        {children}
                      </TextLinkButton>
                    ),
                  })}
                </Text>
              ) : (
                <Text>
                  {t('Error loading the list with search text', {
                    TextLinkButton: (children) => (
                      <TextLinkButton
                        key={`error-loading-${children}`}
                        onClick={() =>
                          reloadJobApplicationsWithLastResultSearchText()
                        }
                      >
                        {children}
                      </TextLinkButton>
                    ),
                    ClearTextLinkButton: (children) => (
                      <TextLinkButton
                        onClick={() => {
                          clearTextAndReloadJobApplications();
                        }}
                        key={`error-loading-with-search${children}`}
                      >
                        {children}
                      </TextLinkButton>
                    ),
                  })}
                </Text>
              )}
            </Box>
          </Alert>
        </Box>
      ) : (
        <>
          <JobApplicationListSection
            actionToolbar={actionToolbar}
            questionnaireId={questionnaireId}
            openBillableDialog={openBillableDialog}
            onChangeApplicationCardState={
              setLastOpenedApplicationCardStateForTracking
            }
            useFiltersFns={useFiltersFns}
            isMustHaveToggleSelected={isMustHaveToggleSelected}
            filtersEventCaptureData={filtersEventCaptureData}
            handleStatusChange={handleStatusChange}
            refetchFacets={refetchFacets}
            homeLocationIdsForTracking={homeLocationIdsForTracking}
          />
          <DetailsView
            isMobile={isMobile}
            uiStore={uiStore}
            questionnaireId={questionnaireId}
            jobApplication={jobApplicationStore.shownDetailsJobApplication}
            onUpdateSuccess={async () => {
              closeDeepLinkedDetailView();
              await refetchFacets();
            }}
            onClose={closeDeepLinkedDetailView}
            applicationCardIsExpanded={
              lastOpenedApplicationCardStateForTracking
            }
            handleStatusChange={handleStatusChange}
            showSuccessMessageToast
          />
        </>
      )}
    </ContentBlock>
  );
};

export default observer(CandidateList);
